import React, { useState } from 'react';

// Define types for our component
interface SqlToJsProps {}

// Define types for aggregation
interface Aggregation {
  type: string;
  field: string;
  alias: string;
}

// SQL conversion examples for reference
const CONVERSION_EXAMPLES = {
  select: {
    sql: 'SELECT * FROM users WHERE age > 18 ORDER BY name ASC;',
    js: `users
  .filter(user => user.age > 18)
  .sort((a, b) => a.name.localeCompare(b.name))`
  },
  join: {
    sql: 'SELECT users.name, orders.total FROM users JOIN orders ON users.id = orders.user_id;',
    js: `users.flatMap(user => 
  orders
    .filter(order => order.user_id === user.id)
    .map(order => ({
      name: user.name,
      total: order.total
    }))
)`
  },
  groupBy: {
    sql: 'SELECT category, COUNT(*) as count, SUM(price) as total FROM products GROUP BY category;',
    js: `Object.values(
  products.reduce((acc, product) => {
    if (!acc[product.category]) {
      acc[product.category] = { 
        category: product.category, 
        count: 0, 
        total: 0 
      };
    }
    acc[product.category].count += 1;
    acc[product.category].total += product.price;
    return acc;
  }, {})
)`
  }
};

// Main component to convert SQL to JavaScript
const SqlToJs: React.FC<SqlToJsProps> = () => {
  // State for the SQL input
  const [sqlInput, setSqlInput] = useState<string>('');
  // State for the JavaScript output
  const [jsOutput, setJsOutput] = useState<string>('');
  // State for explanation text
  const [explanation, setExplanation] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State to track selected example
  const [selectedExample, setSelectedExample] = useState<string>('');
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);

  // Handle SQL input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setSqlInput(e.target.value);
    setError('');
    setSelectedExample('');
  };

  // Function to handle example selection
  const handleExampleSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const exampleKey = e.target.value;
    if (exampleKey && CONVERSION_EXAMPLES[exampleKey as keyof typeof CONVERSION_EXAMPLES]) {
      const example = CONVERSION_EXAMPLES[exampleKey as keyof typeof CONVERSION_EXAMPLES];
      setSqlInput(example.sql);
      setJsOutput(example.js);
      setExplanation(`This is a pre-configured example showing how a ${exampleKey.toUpperCase()} query is converted to JavaScript.`);
      setError('');
      setSelectedExample(exampleKey);
    }
  };

  // Helper function to process WHERE clause
  const processWhereClause = (whereClause: string): string => {
    return whereClause
      // Handle AND, OR operators
      .replace(/\s+and\s+/gi, ' && ')
      .replace(/\s+or\s+/gi, ' || ')
      // Handle string equality 
      .replace(/(\w+)\s*=\s*'([^']*)'/g, "$1 === '$2'")
      .replace(/(\w+)\s*=\s*"([^"]*)"/g, '$1 === "$2"')
      // Handle numeric comparisons
      .replace(/(\w+)\s*=\s*(\d+)/g, '$1 === $2')
      .replace(/(\w+)\s*!=\s*(\d+)/g, '$1 !== $2')
      // Handle column references
      .replace(/(\w+)\.(\w+)/g, '$1.$2');
  };

  // The actual SQL to JS conversion function
  const convertSqlToJavaScript = (sqlQuery: string): { js: string; explanation: string } => {
    sqlQuery = sqlQuery.trim();
    
    // Check if the query is empty
    if (!sqlQuery) {
      return { js: '', explanation: '' };
    }
    
    // Basic SELECT statement parsing
    if (sqlQuery.toLowerCase().startsWith('select')) {
      // Parse the SQL query
      const selectMatch = sqlQuery.match(/select\s+(.*?)\s+from\s+(\w+)(?:\s+where\s+(.*?))?(?:\s+order\s+by\s+(.*?))?(?:\s+limit\s+(\d+))?;?\s*$/i);
      
      if (!selectMatch) {
        throw new Error('Invalid SQL query format. Expected: SELECT columns FROM table [WHERE condition] [ORDER BY columns] [LIMIT number]');
      }
      
      const columns = selectMatch[1].trim();
      const tableName = selectMatch[2].trim();
      const whereClause = selectMatch[3] ? selectMatch[3].trim() : null;
      const orderByClause = selectMatch[4] ? selectMatch[4].trim() : null;
      const limitClause = selectMatch[5] ? parseInt(selectMatch[5].trim()) : null;
      
      // Build the JavaScript code
      let jsCode = tableName;
      let explanation = `Starting with the '${tableName}' array as our data source.`;
      
      // Handle WHERE clause
      if (whereClause) {
        // This is a simplified conversion that handles basic conditions
        const processedWhereClause = processWhereClause(whereClause);
        jsCode += `\n  .filter(${tableName.slice(0, 1)} => ${processedWhereClause})`;
        explanation += `\nApplying a filter to only include elements where ${processedWhereClause}.`;
      }
      
      // Handle ORDER BY clause
      if (orderByClause) {
        const orderParts = orderByClause.split(' ');
        const orderColumn = orderParts[0].trim();
        const isDesc = orderParts.length > 1 && orderParts[1].trim().toLowerCase() === 'desc';
        
        if (isDesc) {
          jsCode += `\n  .sort((a, b) => (b.${orderColumn} > a.${orderColumn}) ? 1 : ((b.${orderColumn} < a.${orderColumn}) ? -1 : 0))`;
          explanation += `\nSorting the results in descending order based on the '${orderColumn}' property.`;
        } else {
          jsCode += `\n  .sort((a, b) => (a.${orderColumn} > b.${orderColumn}) ? 1 : ((a.${orderColumn} < b.${orderColumn}) ? -1 : 0))`;
          explanation += `\nSorting the results in ascending order based on the '${orderColumn}' property.`;
        }
      }
      
      // Handle column selection (if not '*')
      if (columns !== '*') {
        const columnList = columns.split(',').map(col => col.trim());
        
        if (columnList.length === 1) {
          jsCode += `\n  .map(${tableName.slice(0, 1)} => ${tableName.slice(0, 1)}.${columnList[0]})`;
          explanation += `\nExtracting only the '${columnList[0]}' property from each element.`;
        } else {
          const colSelections = columnList.map(col => `    ${col}: ${tableName.slice(0, 1)}.${col}`).join(',\n');
          jsCode += `\n  .map(${tableName.slice(0, 1)} => ({\n${colSelections}\n  }))`;
          explanation += `\nCreating a new object with only the selected properties: ${columnList.join(', ')}.`;
        }
      }
      
      // Handle LIMIT clause
      if (limitClause !== null && !isNaN(limitClause)) {
        jsCode += `\n  .slice(0, ${limitClause})`;
        explanation += `\nLimiting the results to the first ${limitClause} elements.`;
      }
      
      return { js: jsCode, explanation };
    } 
    
    // JOIN handling
    else if (sqlQuery.toLowerCase().includes(' join ')) {
      const joinMatch = sqlQuery.match(/select\s+(.*?)\s+from\s+(\w+)\s+join\s+(\w+)\s+on\s+(.*?)(?:\s+where\s+(.*?))?(?:\s+order\s+by\s+(.*?))?(?:\s+limit\s+(\d+))?;?\s*$/i);
      
      if (!joinMatch) {
        throw new Error('Invalid JOIN query format');
      }
      
      const columns = joinMatch[1].trim();
      const leftTable = joinMatch[2].trim();
      const rightTable = joinMatch[3].trim();
      const joinCondition = joinMatch[4].trim();
      
      // Process join condition to JavaScript syntax
      const processedJoinCondition = joinCondition
        .replace(/(\w+)\.(\w+)\s*=\s*(\w+)\.(\w+)/g, '$3.$4 === $1.$2');
      
      // Process columns
      const columnList = columns.split(',').map(col => {
        const trimmed = col.trim();
        if (trimmed.includes('.')) {
          const [table, field] = trimmed.split('.');
          return { table, field };
        }
        return { table: '', field: trimmed };
      });
      
      // Build field selection mapping
      const fieldMapping = columnList.map(col => {
        if (col.table) {
          return `      ${col.field}: ${col.table}.${col.field}`;
        }
        return `      ${col.field}: item.${col.field}`;
      }).join(',\n');
      
      const jsCode = `${leftTable}.flatMap(${leftTable.slice(0, 1)} => 
  ${rightTable}
    .filter(${rightTable.slice(0, 1)} => ${processedJoinCondition})
    .map(${rightTable.slice(0, 1)} => ({
${fieldMapping}
    }))
)`;
      
      const explanation = `This code performs a join operation between '${leftTable}' and '${rightTable}' arrays. For each element in '${leftTable}', it finds matching elements in '${rightTable}' based on the join condition, and then creates a new object with the selected properties.`;
      
      return { js: jsCode, explanation };
    }
    
    // GROUP BY handling
    else if (sqlQuery.toLowerCase().includes('group by')) {
      const groupByMatch = sqlQuery.match(/select\s+(.*?)\s+from\s+(\w+)(?:\s+where\s+(.*?))?\s+group\s+by\s+(.*?)(?:\s+having\s+(.*?))?(?:\s+order\s+by\s+(.*?))?(?:\s+limit\s+(\d+))?;?\s*$/i);
      
      if (!groupByMatch) {
        throw new Error('Invalid GROUP BY query format');
      }
      
      const columns = groupByMatch[1].trim();
      const tableName = groupByMatch[2].trim();
      const whereClause = groupByMatch[3] ? groupByMatch[3].trim() : null;
      const groupByColumns = groupByMatch[4].trim().split(',').map(col => col.trim());
      
      // Parse the aggregation functions (e.g., COUNT, SUM, AVG)
      const aggregations: Aggregation[] = [];
      const colRegex = /(?:(count|sum|avg|min|max)\(([^)]*)\)(?:\s+as\s+(\w+))?)|(\w+)/gi;
      let colMatch;
      
      while ((colMatch = colRegex.exec(columns)) !== null) {
        if (colMatch[4]) {  // This is a regular column (non-aggregation)
          aggregations.push({
            type: 'field',
            field: colMatch[4],
            alias: colMatch[4]
          });
        } else {  // This is an aggregation function
          aggregations.push({
            type: colMatch[1].toLowerCase(),
            field: colMatch[2].trim() === '*' ? '' : colMatch[2].trim(),
            alias: colMatch[3] || `${colMatch[1].toLowerCase()}_${colMatch[2].trim()}`
          });
        }
      }
      
      // Build the group by key
      const groupKey = groupByColumns.length === 1 
        ? `item.${groupByColumns[0]}` 
        : `[${groupByColumns.map(col => `item.${col}`).join(', ')}].join('_')`;
      
      // Build the initialization code for the accumulator
      let initCode = '';
      let reduceCode = '';
      
      aggregations.forEach(agg => {
        if (agg.type === 'field') {
          if (agg.field && groupByColumns.includes(agg.field)) {
            initCode += `        ${agg.alias}: item.${agg.field},\n`;
          }
        } else if (agg.type === 'count') {
          initCode += `        ${agg.alias}: 0,\n`;
          reduceCode += `      acc[key].${agg.alias} += 1;\n`;
        } else if (agg.type === 'sum') {
          initCode += `        ${agg.alias}: 0,\n`;
          reduceCode += `      acc[key].${agg.alias} += item.${agg.field};\n`;
        } else if (agg.type === 'avg') {
          initCode += `        ${agg.alias}_sum: 0,\n`;
          initCode += `        ${agg.alias}_count: 0,\n`;
          initCode += `        get ${agg.alias}() { return this.${agg.alias}_count === 0 ? 0 : this.${agg.alias}_sum / this.${agg.alias}_count; },\n`;
          reduceCode += `      acc[key].${agg.alias}_sum += item.${agg.field};\n`;
          reduceCode += `      acc[key].${agg.alias}_count += 1;\n`;
        } else if (agg.type === 'min') {
          initCode += `        ${agg.alias}: Infinity,\n`;
          reduceCode += `      acc[key].${agg.alias} = Math.min(acc[key].${agg.alias}, item.${agg.field});\n`;
        } else if (agg.type === 'max') {
          initCode += `        ${agg.alias}: -Infinity,\n`;
          reduceCode += `      acc[key].${agg.alias} = Math.max(acc[key].${agg.alias}, item.${agg.field});\n`;
        }
      });
      
      // Build the JavaScript for GROUP BY
      let jsCode = `// First, filter the data if needed\n`;
      if (whereClause) {
        jsCode += `const filtered = ${tableName}.filter(item => ${processWhereClause(whereClause)});\n\n`;
      } else {
        jsCode += `const filtered = ${tableName};\n\n`;
      }
      
      jsCode += `// Then, group and aggregate the data\n`;
      jsCode += `const result = Object.values(\n`;
      jsCode += `  filtered.reduce((acc, item) => {\n`;
      jsCode += `    const key = ${groupKey};\n`;
      jsCode += `    if (!acc[key]) {\n`;
      jsCode += `      acc[key] = {\n`;
      jsCode += initCode;
      jsCode += `      };\n`;
      jsCode += `    }\n`;
      jsCode += reduceCode;
      jsCode += `    return acc;\n`;
      jsCode += `  }, {})\n`;
      jsCode += `)`;
      
      const explanation = `This code performs a GROUP BY operation on '${tableName}'. It groups the data by ${groupByColumns.join(', ')} and calculates the specified aggregations. The result is an array of objects, each representing a group.`;
      
      return { js: jsCode, explanation };
    }
    
    else {
      throw new Error('Only SELECT queries with optional WHERE, ORDER BY, GROUP BY, and JOIN clauses are supported');
    }
  };

  // Convert SQL to JavaScript button handler
  const handleConvertClick = () => {
    setError('');
    setIsLoading(true);
    setJsOutput('');
    setExplanation('');

    if (!sqlInput.trim()) {
      setError('Please enter an SQL query');
      setIsLoading(false);
      return;
    }

    try {
      // If we're using a pre-defined example, just show that directly
      if (selectedExample && CONVERSION_EXAMPLES[selectedExample as keyof typeof CONVERSION_EXAMPLES]) {
        const example = CONVERSION_EXAMPLES[selectedExample as keyof typeof CONVERSION_EXAMPLES];
        setJsOutput(example.js);
        setExplanation(`This is a pre-configured example showing how a ${selectedExample.toUpperCase()} query is converted to JavaScript.`);
      } else {
        // Otherwise, convert the SQL dynamically
        const result = convertSqlToJavaScript(sqlInput);
        setJsOutput(result.js);
        setExplanation(result.explanation);
      }

      setIsLoading(false);
    } catch (e) {
      setError(`Error converting SQL: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setSqlInput('');
    setJsOutput('');
    setExplanation('');
    setError('');
    setSelectedExample('');
  };

  // Copy JavaScript to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(jsOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">SQL to JavaScript Converter</h1>

      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert SQL queries to equivalent JavaScript code. This tool helps you translate SQL operations into JavaScript array methods.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="sqlInput" className="font-medium text-gray-700">
                SQL Query
              </label>
              <div className="flex space-x-2">
                <select
                  className="text-sm border border-gray-300 rounded-md p-1"
                  value={selectedExample}
                  onChange={handleExampleSelection}
                >
                  <option value="">-- Use Example --</option>
                  <option value="select">SELECT Example</option>
                  <option value="join">JOIN Example</option>
                  <option value="groupBy">GROUP BY Example</option>
                </select>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setSqlInput('')}
                >
                  Clear
                </button>
              </div>
            </div>

            <textarea
              id="sqlInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={sqlInput}
              onChange={handleInputChange}
              placeholder="Enter your SQL query here, e.g.,
SELECT * FROM users WHERE age > 18 ORDER BY name;"
            />
          </div>

          <div className="mt-4 flex justify-between">
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              onClick={handleConvertClick}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </div>
              ) : (
                'Convert to JavaScript'
              )}
            </button>

            <button
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              onClick={clearAll}
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              JavaScript Output
            </label>

            {jsOutput && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy Code'}
                </button>
              </div>
            )}
          </div>

          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}

            {!error && !jsOutput && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p>
                    JavaScript code will appear here after converting the SQL query.
                  </p>
                </div>
              </div>
            )}

            {!error && jsOutput && (
              <div className="flex-grow flex flex-col">
                <pre className="flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm overflow-auto bg-gray-50">
                  {jsOutput}
                </pre>
                
                {explanation && (
                  <div className="mt-3 bg-blue-50 p-3 rounded-md border border-blue-100 text-sm">
                    <div className="font-medium mb-1">Explanation:</div>
                    <p>{explanation}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About SQL to JavaScript Conversion</h2>

        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">What This Tool Does</h3>
            <p className="text-gray-600">
              This tool translates SQL queries into equivalent JavaScript code using array methods. It helps developers who are familiar with SQL to implement similar data operations in JavaScript applications.
            </p>
          </div>

          <div>
            <h3 className="font-medium text-gray-800 mb-2">Supported SQL Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>SELECT</strong> - Converted to map() or object creation</li>
              <li><strong>FROM</strong> - The source array</li>
              <li><strong>WHERE</strong> - Converted to filter()</li>
              <li><strong>ORDER BY</strong> - Converted to sort()</li>
              <li><strong>LIMIT</strong> - Converted to slice()</li>
              <li><strong>JOIN</strong> - Implemented with flatMap() and filter()</li>
              <li><strong>GROUP BY</strong> - Implemented with reduce() and Object.values()</li>
              <li><strong>Aggregations</strong> - COUNT, SUM, AVG, MIN, MAX implemented within reduce()</li>
            </ul>
          </div>

          <div>
            <h3 className="font-medium text-gray-800 mb-2">SQL vs JavaScript Paradigms</h3>
            <p className="text-gray-600">
              SQL is a declarative language designed for querying relational databases, while JavaScript uses imperative and functional programming paradigms. This converter bridges the gap by translating SQL's declarative style into JavaScript's functional programming patterns, primarily using array methods like map(), filter(), reduce(), and sort().
            </p>
          </div>

          <div>
            <h3 className="font-medium text-gray-800 mb-2">Limitations</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Only supports basic SQL operations (no subqueries, complex JOINs, or UNION)</li>
              <li>Limited support for complex WHERE conditions</li>
              <li>No support for transactions or database-specific features</li>
              <li>Assumes data is already available as JavaScript arrays</li>
              <li>Performance may differ from optimized SQL queries on large datasets</li>
            </ul>
          </div>

          <div>
            <h3 className="font-medium text-gray-800 mb-2">Usage Tips</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use this tool as a starting point and customize the generated code as needed</li>
              <li>For complex data operations, consider using a dedicated JavaScript library like lodash</li>
              <li>When working with very large datasets, consider server-side processing with SQL instead</li>
              <li>The generated code assumes your data structure matches the column names in your SQL query</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SqlToJs;